import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

// Bundle-tiers PDP widget (Stage 1: UI only).
// Tier cards (1/2/3 units) with per-unit engraving text fields. On Add to
// cart it adds one line item per engraved unit (each carrying its own
// properties[Engraving]) via /cart/add.js with an items array, then opens
// the cart drawer. All units use the CURRENTLY selected variant, so any
// engraving upcharge in the picker applies to every unit. When it
// initializes it takes over the buy flow: the native buy-buttons and any
// in-section lm-engraving block are hidden. If this module fails to load
// (e.g. a locked-down in-app browser), those native controls stay visible
// and usable — no dead end.
//
// Two integrity rules the widget must never break (review 2026-08-13):
//   P0-1 — it must not offer an add the cart would refuse. When the selected
//   variant is unavailable we hide our button and hand the flow BACK to core's
//   buy form, whose disabled state carries inventory and pickup messaging ours
//   does not.
//   P0-2 — engraving text and the priced variant must agree. Text in a visible
//   field drives the engraving variant (same contract as lm-engraving.js), and
//   buildItems() refuses to attach properties.Engraving to a non-engraving
//   variant even if that drive never happened.

const CART_ADD_URL = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
const CART_URL = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';

const ENGRAVING_SOLD_OUT = 'Engraving is sold out right now.';
const ENGRAVING_MISMATCH =
  'You picked the plain option, so the engraving text will not be added. Edit a field to switch back.';

if (!(window.customElements && customElements.get('lm-bundle-tiers'))) {
  class LmBundleTiers extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || '';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.currency = this.dataset.currency || 'USD';
      this.showClaims = this.dataset.showClaims === 'true';
      this.currentVariantId = this.dataset.defaultVariantId || '';

      // Engraving/variant coupling. Only set when the product actually exposes
      // the engraving option, so products without it keep the plain behaviour.
      this.engravingVariantMode = this.dataset.engravingVariantMode === 'true';
      this.optionName = this.dataset.optionName || '';
      this.onValue = this.dataset.onValue || '';
      this.offValue = this.dataset.offValue || '';

      this.tiers = Array.from(this.querySelectorAll('[data-lm-tier]'));
      this.radios = Array.from(this.querySelectorAll('[data-lm-tier-radio]'));
      this.engraveRows = Array.from(this.querySelectorAll('[data-lm-engrave-row]'));
      this.addButton = this.querySelector('[data-lm-bundle-add]');
      this.errorEl = this.querySelector('[data-lm-bundle-error]');
      this.noticeEl = this.querySelector('[data-lm-bundle-notice]');

      // Without the core controls there is nothing to enhance — bail and
      // leave the native buy flow untouched.
      if (!this.addButton || !this.radios.length) return;

      this.addLabel = this.addButton.dataset.addLabel || this.addButton.textContent.trim();
      this.unavailableLabel = this.addButton.dataset.unavailableLabel || this.addLabel;
      // Server-rendered verdict, used when nothing better is knowable.
      this.serverCanAdd = !this.addButton.disabled;

      this.variants = {};
      const variantScript = this.querySelector('[data-lm-bundle-variants]');
      if (variantScript) {
        try {
          this.variants = JSON.parse(variantScript.textContent) || {};
        } catch (e) {
          this.variants = {};
        }
      }

      this.sectionEl = document.getElementById('shopify-section-' + this.sectionId);
      this.scope = this.sectionEl || document;
      this.variantPicker = this.findVariantPicker();
      this.syncCurrentVariant();

      // Bundle owns the engraving UI whenever it renders — hide the competing
      // standalone block for good, regardless of availability.
      this.hideStandaloneEngraving();

      this._onTierChange = this.onTierChange.bind(this);
      this.radios.forEach((r) => r.addEventListener('change', this._onTierChange));

      this._onEngraveInput = this.onEngraveInput.bind(this);
      this.engraveRows.forEach((row) => {
        const input = row.querySelector('[data-lm-engrave-input]');
        if (input) input.addEventListener('input', this._onEngraveInput);
      });

      this._onAdd = this.onAdd.bind(this);
      this.addButton.addEventListener('click', this._onAdd);

      // Recompute prices when the shopper switches variant.
      this._onVariantUpdate = this.onVariantUpdate.bind(this);
      document.addEventListener('variant:update', this._onVariantUpdate);

      this.updateRows();
      this.recomputePrices();
      this._engravingIntent = this.engravingIntent();
      this.updateEngravingState();

      // Reveal our button now that JS is live — but only if we can actually sell.
      this.applyAvailability(this.resolveAvailability());
    }

    disconnectedCallback() {
      if (this._onTierChange) this.radios.forEach((r) => r.removeEventListener('change', this._onTierChange));
      if (this._onEngraveInput) {
        this.engraveRows.forEach((row) => {
          const input = row.querySelector('[data-lm-engrave-input]');
          if (input) input.removeEventListener('input', this._onEngraveInput);
        });
      }
      if (this.addButton && this._onAdd) this.addButton.removeEventListener('click', this._onAdd);
      if (this._onVariantUpdate) document.removeEventListener('variant:update', this._onVariantUpdate);
    }

    findVariantPicker() {
      const pickers = document.querySelectorAll('variant-picker');
      for (let i = 0; i < pickers.length; i++) {
        if (pickers[i].dataset.sectionId === this.sectionId) return pickers[i];
      }
      return pickers[0] || null;
    }

    syncCurrentVariant() {
      let id = null;
      if (this.variantPicker && this.variantPicker.selectedOption) {
        id = this.variantPicker.selectedOption.dataset.variantId;
      }
      if (!id) {
        const input = this.scope.querySelector('input[name="id"]') || document.querySelector('input[name="id"]');
        if (input) id = input.value;
      }
      if (id) this.currentVariantId = String(id);
    }

    // Native add-to-cart: the product-form-component wrapping the buy-buttons
    // form in this section (covers add-to-cart, quantity and dynamic checkout).
    // Falls back to the form element itself.
    nativeFormEl() {
      if (this._nativeForm !== undefined) return this._nativeForm;
      const form = this.scope.querySelector('form[id^="BuyButtons-ProductForm-"]');
      this._nativeForm = form ? form.closest('product-form-component') || form : null;
      return this._nativeForm;
    }

    setNativeFormHidden(hidden) {
      const el = this.nativeFormEl();
      if (!el) return;
      el.style.display = hidden ? 'none' : '';
    }

    hideStandaloneEngraving() {
      const eng =
        this.scope.querySelector('lm-engraving[data-section-id="' + this.sectionId + '"]') ||
        this.scope.querySelector('lm-engraving');
      if (eng) eng.style.display = 'none';
    }

    // Availability of the currently selected variant, from the server-rendered
    // map. Falls back to the server's own verdict when the id is unknown (a
    // combined-listing swap, or a map that failed to parse) so an unreadable
    // map can never silently re-enable a sold-out button.
    resolveAvailability() {
      const v = this.variants[this.currentVariantId];
      if (v && typeof v.available === 'boolean') return v.available;
      return this.serverCanAdd;
    }

    // The single gate for P0-1. Unavailable => our button goes away and core's
    // buy form comes back to own the sold-out state.
    applyAvailability(available) {
      this.available = available !== false;
      this.setNativeFormHidden(this.available);

      this.addButton.disabled = !this.available;
      this.addButton.textContent = this.available ? this.addLabel : this.unavailableLabel;
      if (this.available) this.addButton.removeAttribute('hidden');
      else this.addButton.setAttribute('hidden', '');
    }

    selectedUnits() {
      const checked = this.radios.find((r) => r.checked) || this.radios[0];
      const units = parseInt(checked && checked.value, 10);
      return units > 0 ? units : 1;
    }

    onTierChange() {
      this.clearError();
      this.updateRows();
      // A smaller tier can hide a filled field, which changes the engraving
      // intent — re-evaluate before the shopper reaches the button.
      this.syncEngravingVariant();
    }

    onEngraveInput(event) {
      const row = event.target.closest('[data-lm-engrave-row]');
      if (row) {
        const remaining = row.querySelector('[data-lm-engrave-remaining]');
        // The maxlength attribute is the single source of truth for the limit
        // (review P2-6) — no fallback number lives here.
        const max = parseInt(event.target.getAttribute('maxlength'), 10);
        if (remaining && max > 0) {
          let left = max - event.target.value.length;
          if (left < 0) left = 0;
          remaining.textContent = String(left);
        }
      }
      this.syncEngravingVariant();
    }

    onVariantUpdate(event) {
      const resource = event && event.detail ? event.detail.resource : undefined;
      if (resource && resource.id) {
        this.currentVariantId = String(resource.id);
      } else {
        this.syncCurrentVariant();
      }

      // Core treats a null resource as unavailable (assets/product-form.js) —
      // match it rather than inventing our own rule.
      let available;
      if (resource === null) available = false;
      else if (resource && typeof resource.available === 'boolean') available = resource.available;
      else available = this.resolveAvailability();

      this.recomputePrices();
      this.updateEngravingState();
      this.applyAvailability(available);
    }

    // Show one engraving field per unit of the selected tier.
    updateRows() {
      const units = this.selectedUnits();
      this.engraveRows.forEach((row) => {
        const idx = parseInt(row.dataset.unitIndex, 10) || 0;
        if (idx > 0 && idx <= units) row.removeAttribute('hidden');
        else row.setAttribute('hidden', '');
      });
    }

    recomputePrices() {
      const variant = this.variants[this.currentVariantId];
      const unitPrice = variant && variant.price;
      if (typeof unitPrice !== 'number') return; // leave server-rendered values

      this.tiers.forEach((tier) => {
        const units = parseInt(tier.dataset.units, 10) || 0;
        // parseFloat, not parseInt: percentages can be fractional (e.g. 33.33).
        const percent = parseFloat(tier.dataset.percent) || 0;
        if (units < 1) return;

        const total = unitPrice * units;
        const totalEl = tier.querySelector('[data-lm-tier-total]');
        const compareEl = tier.querySelector('[data-lm-tier-compare]');

        if (this.showClaims && percent > 0) {
          const discounted = Math.round((total * (100 - percent)) / 100);
          if (compareEl) compareEl.textContent = this.money(total);
          if (totalEl) totalEl.textContent = this.money(discounted);
        } else if (totalEl) {
          totalEl.textContent = this.money(total);
        }
      });
    }

    money(cents) {
      try {
        return formatMoney(cents, this.moneyFormat, this.currency);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    /* ---------- engraving <-> variant coupling (P0-2) ---------- */

    engraveInputs() {
      return this.engraveRows
        .map((row) => row.querySelector('[data-lm-engrave-input]'))
        .filter((input) => input !== null);
    }

    // Only the rows the selected tier actually shows count as intent — a filled
    // field hidden by a smaller tier is not submitted, so it must not price.
    engravingIntent() {
      return this.engraveRows.some((row) => {
        if (row.hasAttribute('hidden')) return false;
        const input = row.querySelector('[data-lm-engrave-input]');
        return !!input && input.value.trim() !== '';
      });
    }

    // Drive the picker only when the intent FLIPS, so a 20-character name does
    // not fire 20 section fetches. Nothing here runs from variant:update, so
    // this can never loop with the picker.
    syncEngravingVariant() {
      if (!this.engravingVariantMode) return;
      const intent = this.engravingIntent();
      if (intent === this._engravingIntent) return;
      this._engravingIntent = intent;
      this.driveVariant(intent ? this.onValue : this.offValue);
    }

    findRadio(value) {
      if (!this.variantPicker) return null;
      const radios = this.variantPicker.querySelectorAll('input[type="radio"]');
      // Prefer the radio that belongs to the engraving option fieldset.
      for (let i = 0; i < radios.length; i++) {
        if (radios[i].value === value && radios[i].name.indexOf(this.optionName + '-') === 0) {
          return radios[i];
        }
      }
      // Fallback: single-option products only have one fieldset.
      for (let i = 0; i < radios.length; i++) {
        if (radios[i].value === value) return radios[i];
      }
      return null;
    }

    // Ported from assets/lm-engraving.js:120-146 — same contract, so the two
    // blocks drive the picker identically.
    driveVariant(value) {
      if (!value) return;

      const radio = this.findRadio(value);
      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      // Dropdown variant style.
      const select = this.variantPicker ? this.variantPicker.querySelector('select') : null;
      if (!select) return;
      for (let i = 0; i < select.options.length; i++) {
        if (select.options[i].value === value) {
          if (select.selectedIndex !== i) {
            select.selectedIndex = i;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      }
    }

    // Engraving option value of the current variant. Prefers the server map;
    // falls back to reading the picker so an unparsed map cannot strip an
    // engraving the shopper has already paid the upcharge for.
    currentEngravingValue() {
      const variant = this.variants[this.currentVariantId];
      if (variant && typeof variant.engraving === 'string') return variant.engraving;

      const onRadio = this.findRadio(this.onValue);
      if (onRadio) return onRadio.checked ? this.onValue : this.offValue;
      const select = this.variantPicker ? this.variantPicker.querySelector('select') : null;
      if (select) return select.value;
      return null;
    }

    // True unless EVERY engraved variant is sold out.
    engravedVariantAvailable() {
      const ids = Object.keys(this.variants);
      let found = false;
      for (let i = 0; i < ids.length; i++) {
        const variant = this.variants[ids[i]];
        if (!variant || variant.engraving !== this.onValue) continue;
        found = true;
        if (variant.available !== false) return true;
      }
      return !found;
    }

    setEngraveInputsDisabled(disabled) {
      this.engraveInputs().forEach((input) => {
        input.disabled = disabled;
      });
    }

    updateEngravingState() {
      if (!this.engravingVariantMode) return;

      if (!this.engravedVariantAvailable()) {
        this.setEngraveInputsDisabled(true);
        this.showNotice(ENGRAVING_SOLD_OUT);
        return;
      }
      this.setEngraveInputsDisabled(false);

      if (this.currentEngravingValue() !== this.onValue && this.engravingIntent()) {
        // The shopper picked the plain option by hand. Don't fight them and
        // don't destroy their text — just stop pretending it will be engraved.
        // Resetting the remembered intent lets the next keystroke drive back.
        this._engravingIntent = false;
        this.showNotice(ENGRAVING_MISMATCH);
        return;
      }
      this.clearNotice();
    }

    /* ---------- cart ---------- */

    buildItems() {
      const units = this.selectedUnits();
      const id = Number(this.currentVariantId);
      // Hard guard for P0-2: the engraving property may only ride on the
      // engraved variant. This holds even if driveVariant never ran.
      const allowEngraving = !this.engravingVariantMode || this.currentEngravingValue() === this.onValue;
      const items = [];
      for (let i = 1; i <= units; i++) {
        const item = { id: id, quantity: 1 };
        const row = this.engraveRows.find((r) => parseInt(r.dataset.unitIndex, 10) === i);
        if (row && allowEngraving) {
          const input = row.querySelector('[data-lm-engrave-input]');
          const text = input && input.value.trim();
          // Each engraved unit carries its own property; blank units omit it
          // (Shopify merges identical blank lines into one).
          if (text) item.properties = { Engraving: text };
        }
        items.push(item);
      }
      return items;
    }

    // Read the ticked lm-product-addons rows straight from the DOM (the same stable
    // data-attribute contract lm-addons.js uses) so this still works if lm-addons.js never
    // upgraded the element in a locked-down in-app browser. Mirrors
    // LmProductAddons.checkedItems(): same variant source + _addon line-item properties.
    collectAddonItems() {
      const el = document.querySelector('lm-product-addons');
      if (!el) return [];
      const handle = el.dataset.mainHandle || '';
      const items = [];
      el.querySelectorAll('[data-lm-addon-row]').forEach((row) => {
        const check = row.querySelector('[data-lm-addon-check]');
        if (!check || !check.checked) return;
        const id = Number(row.dataset.variantId);
        if (!id) return;
        items.push({ id: id, quantity: 1, properties: { _addon: 'true', _addon_for: handle } });
      });
      return items;
    }

    // Untick the add-ons after a successful add so a second bundle ATC doesn't re-add them
    // (parity with lm-addons.js clearChecks() after the native flow).
    clearAddonChecks() {
      const el = document.querySelector('lm-product-addons');
      if (!el) return;
      el.querySelectorAll('[data-lm-addon-check]:checked').forEach((c) => {
        c.checked = false;
      });
    }

    gatherSectionIds() {
      const ids = [];
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
      });
      return ids.join(',');
    }

    onAdd() {
      if (this._busy) return;
      // Belt and braces behind the disabled attribute: the sticky bar proxies
      // clicks here, and a programmatic click on a hidden control must not sell
      // something we already know is unavailable.
      if (this.available === false) return;
      this.clearError();

      const id = Number(this.currentVariantId);
      if (!id) {
        this.showError('Please choose your options first.');
        return;
      }

      const items = this.buildItems();
      if (!items.length) return;

      // Fold any ticked "Make it complete" add-ons into the SAME cart/add.js call so the
      // bundle and its add-ons land atomically. The bundle owns the buy flow and hides the
      // native form that lm-addons.js normally piggybacks on, so we collect them here.
      const addonItems = this.collectAddonItems();
      if (addonItems.length) items.push(...addonItems);

      this._busy = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.disabled = true;

      const payload = { items: items, sections: this.gatherSectionIds() };

      fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response && response.status) {
            // Shopify error payload (e.g. sold out / not enough stock).
            this.showError(response.description || response.message || 'Could not add to cart.');
            return null;
          }
          this.clearAddonChecks();
          return this.refreshAndOpenCart(response, items);
        })
        .catch(() => {
          this.showError('Something went wrong. Please try again.');
        })
        .finally(() => {
          this._busy = false;
          this.addButton.removeAttribute('aria-busy');
          // Never re-enable a button the availability gate has closed.
          this.addButton.disabled = this.available === false;
        });
    }

    refreshAndOpenCart(addResponse, items) {
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      // Fetch the cart so the drawer gets an accurate item_count (mirrors
      // product-form.js), then dispatch the event the cart drawer listens for.
      return fetch(CART_URL + '.js')
        .then((r) => r.json())
        .catch(() => null)
        .then((cart) => {
          document.dispatchEvent(
            new CartAddEvent(cart || undefined, this.id || 'lm-bundle-tiers', {
              source: 'lm-bundle-tiers',
              itemCount: totalQuantity,
              sections: addResponse && addResponse.sections,
            })
          );
        });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.removeAttribute('hidden');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.setAttribute('hidden', '');
    }

    showNotice(message) {
      if (!this.noticeEl) return;
      this.noticeEl.textContent = message;
      this.noticeEl.removeAttribute('hidden');
    }

    clearNotice() {
      if (!this.noticeEl) return;
      this.noticeEl.textContent = '';
      this.noticeEl.setAttribute('hidden', '');
    }
  }

  customElements.define('lm-bundle-tiers', LmBundleTiers);
}
